import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {collectFormatoQuestionOptions} from 'src/modules/tarea/formatoQuestionOptions';
import AsignadorFuncionAutocompleteFormItem from 'src/view/asignadorFuncion/autocomplete/AsignadorFuncionAutocompleteFormItem';
import ModeloTareaAutocompleteFormItem from 'src/view/modeloTarea/autocomplete/ModeloTareaAutocompleteFormItem';
import UbicacionAutocompleteFormItem from 'src/view/ubicacion/autocomplete/UbicacionAutocompleteFormItem';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';

type Props = {
  name: string; // "conditionalRules"
  formQuestionsPath: string; // "formato"
  disabled?: boolean;
};

const ACTION_OPTIONS = [
  {value: 'SIGNATURE', label: 'Solicitar firma'},
  {value: 'TASK', label: 'Enviar tarea'},
  {value: 'EMAIL', label: 'Enviar correo'},
  {value: 'NOTIFICATION', label: 'Enviar notificación'},
] as const;

const MATCH_OPTIONS_TEXT = [
  {value: 'CONTAINS', label: 'Contiene'},
  {value: 'EQUALS', label: 'Es igual a'},
  {value: 'NOT_EQUALS', label: 'No es igual a'},
  {value: 'STARTS_WITH', label: 'Empieza con'},
  {value: 'ENDS_WITH', label: 'Termina con'},
  {value: 'REGEX', label: 'Regex'},
] as const;

const MATCH_OPTIONS_EMPTY = [
  {value: 'IS_EMPTY', label: 'Está vacío'},
  {value: 'IS_NOT_EMPTY', label: 'No está vacío'},
] as const;

const MATCH_OPTIONS_NUMERIC = [
  {value: 'GREATER_THAN', label: 'Mayor que'},
  {value: 'LESS_THAN', label: 'Menor que'},
  {value: 'GREATER_OR_EQUAL', label: 'Mayor o igual que'},
  {value: 'LESS_OR_EQUAL', label: 'Menor o igual que'},
] as const;

const MATCH_OPTIONS = [
  ...MATCH_OPTIONS_TEXT,
  ...MATCH_OPTIONS_EMPTY,
  ...MATCH_OPTIONS_NUMERIC,
] as const;

const EMPTY_OPERATORS = new Set(['IS_EMPTY', 'IS_NOT_EMPTY']);
const NUMERIC_OPERATORS = new Set([
  'GREATER_THAN',
  'LESS_THAN',
  'GREATER_OR_EQUAL',
  'LESS_OR_EQUAL',
]);

type RecipientMode = 'USER' | 'FROM_QUESTION' | 'EMAIL' | 'EXECUTOR' | 'LOCATION_RESPONSIBLE' | 'TEAM';
type RuleActionType = 'SIGNATURE' | 'TASK' | 'EMAIL' | 'NOTIFICATION';
type Combinator = 'AND' | 'OR';
type QuestionOption = {key: string; label: string; title: string};

const emptyLeaf = () => ({
  questionKey: '',
  matchMode: 'CONTAINS',
  matchText: '',
});

const emptyGroup = () => ({
  combinator: 'AND' as Combinator,
  conditions: [emptyLeaf()],
});

const emptyRule = () => ({
  enabled: true,
  groupCombinator: 'OR' as Combinator,
  groups: [emptyGroup()],
  questionKey: '',
  matchMode: 'CONTAINS',
  matchText: '',
  actions: [],
  recipients: [{mode: 'USER', value: '', actions: []}],
  validatorRecipients: [],
  modeloTarea: null,
  taskDueDays: 3,
  emailHighlightQuestionKeys: [],
  attachTaskPdf: true,
  emailSeparateRecipients: false,
});

const VALIDATOR_RECIPIENT_MODES: RecipientMode[] = [
  'FROM_QUESTION',
  'EXECUTOR',
  'USER',
  'LOCATION_RESPONSIBLE',
  'TEAM',
];

const RECIPIENT_MODE_OPTIONS: {value: RecipientMode; label: string}[] = [
  {value: 'FROM_QUESTION', label: 'Desde pregunta'},
  {value: 'EXECUTOR', label: 'Ejecutor de la tarea'},
  {value: 'USER', label: 'Usuario'},
  {value: 'LOCATION_RESPONSIBLE', label: 'Responsable de ubicación'},
  {value: 'TEAM', label: 'Team'},
  {value: 'EMAIL', label: 'Email'},
];

const wrapLegacyGroups = (rule: any) => {
  if (Array.isArray(rule?.groups) && rule.groups.length) return rule.groups;
  return [
    {
      combinator: 'AND' as Combinator,
      conditions: [
        {
          questionKey: rule?.questionKey ?? '',
          matchMode: rule?.matchMode ?? 'CONTAINS',
          matchText: rule?.matchText ?? '',
        },
      ],
    },
  ];
};

const cloneRule = (src: any) => ({
  ...src,
  groups: wrapLegacyGroups(src).map((g: any) => ({
    combinator: g?.combinator === 'OR' ? 'OR' : 'AND',
    conditions: Array.isArray(g?.conditions)
      ? g.conditions.map((c: any) => ({...c}))
      : [emptyLeaf()],
  })),
  groupCombinator: src?.groupCombinator === 'AND' ? 'AND' : 'OR',
  recipients: Array.isArray(src?.recipients)
    ? src.recipients.map((r: any) => ({
        ...r,
        actions: Array.isArray(r?.actions) ? [...r.actions] : r?.actions,
      }))
    : [{mode: 'USER', value: '', actions: []}],
  validatorRecipients: Array.isArray(src?.validatorRecipients)
    ? src.validatorRecipients.map((r: any) => ({
        ...r,
        actions: Array.isArray(r?.actions) ? [...r.actions] : r?.actions,
      }))
    : [],
  emailHighlightQuestionKeys: Array.isArray(src?.emailHighlightQuestionKeys)
    ? [...src.emailHighlightQuestionKeys]
    : src?.emailHighlightQuestionKeys,
  attachTaskPdf: src?.attachTaskPdf !== false,
  emailSeparateRecipients: src?.emailSeparateRecipients === true,
});

const normalizeMulti = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string')
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
};

function getMatchModeLabel(mode: string) {
  const all = [...MATCH_OPTIONS_TEXT, ...MATCH_OPTIONS_EMPTY, ...MATCH_OPTIONS_NUMERIC];
  return all.find((o) => o.value === mode)?.label ?? mode;
}

const nestedPanelSx = {
  px: 1,
  py: 1,
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 1,
  bgcolor: 'rgba(0,0,0,0.02)',
  minWidth: 0,
  '& .MuiFormControl-root': {
    mt: '0px !important',
    mb: '0px !important',
  },
};

const compactControlSx = {
  mt: '0px !important',
  mb: '0px !important',
};

const sectionTitleSx = {fontSize: 13, mb: 1.5};

function summarizeLeaf(leaf: any, questionOptions: QuestionOption[]) {
  if (!leaf) return '';
  const option = leaf.questionKey
    ? questionOptions.find((x) => x.key === leaf.questionKey)
    : undefined;
  const qTitle = option?.title || '';
  const modeLabel = getMatchModeLabel(leaf.matchMode);
  const hasText = !!(leaf.matchText || '').trim();
  const matchPart = EMPTY_OPERATORS.has(leaf.matchMode)
    ? modeLabel
    : hasText
      ? `${modeLabel} "${leaf.matchText}"`
      : modeLabel;
  return qTitle ? `${qTitle} ${matchPart}` : matchPart;
}

function summarizeConditions(rule: any, questionOptions: QuestionOption[]) {
  const groups =
    Array.isArray(rule?.groups) && rule.groups.length
      ? rule.groups
      : [{combinator: 'AND', conditions: [rule]}];
  const groupJoiner = String(rule?.groupCombinator).toUpperCase() === 'AND' ? ' Y ' : ' O ';
  const parts = groups
    .map((g: any) => {
      const conds = Array.isArray(g?.conditions) ? g.conditions : [];
      const innerJoiner = String(g?.combinator).toUpperCase() === 'OR' ? ' O ' : ' Y ';
      const inner = conds
        .map((c: any) => summarizeLeaf(c, questionOptions))
        .filter(Boolean)
        .join(innerJoiner);
      return groups.length > 1 && conds.length > 1 ? `(${inner})` : inner;
    })
    .filter(Boolean);
  return parts.join(groupJoiner) || 'sin condiciones';
}

function CombinatorToggle({
  name,
  disabled,
  fallback = 'AND',
  inset = false,
}: {
  name: string;
  disabled?: boolean;
  fallback?: Combinator;
  inset?: boolean;
}) {
  const {control} = useFormContext();
  return (
    <Box sx={{display: 'flex', justifyContent: 'flex-start', pl: inset ? '42px' : 1.5}}>
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <Box aria-hidden sx={{height: 6, borderLeft: '1px solid', borderColor: 'divider'}} />
        <Controller
          control={control}
          name={name}
          render={({field}) => (
            <ToggleButtonGroup
              exclusive
              size="small"
              disabled={disabled}
              sx={{bgcolor: 'background.paper'}}
              value={field.value === 'OR' || field.value === 'AND' ? field.value : fallback}
              onChange={(_, v) => {
                if (v) field.onChange(v);
              }}>
              <ToggleButton value="AND" sx={{px: 1.25, py: 0.15, lineHeight: 1.2, fontSize: 12, fontWeight: 700}}>
                Y
              </ToggleButton>
              <ToggleButton value="OR" sx={{px: 1.25, py: 0.15, lineHeight: 1.2, fontSize: 12, fontWeight: 700}}>
                O
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        />
        <Box aria-hidden sx={{height: 6, borderLeft: '1px solid', borderColor: 'divider'}} />
      </Box>
    </Box>
  );
}

function ConditionRow({
  baseName,
  questionOptions,
  disabled,
  onRemove,
  canRemove,
  dragHandleProps,
}: {
  baseName: string;
  questionOptions: QuestionOption[];
  disabled: boolean;
  onRemove: () => void;
  canRemove: boolean;
  dragHandleProps?: any;
}) {
  const {control} = useFormContext();
  const matchMode = useWatch({control, name: `${baseName}.matchMode`}) || 'CONTAINS';
  const needsMatchValue = !EMPTY_OPERATORS.has(matchMode);
  const isNumericOperator = NUMERIC_OPERATORS.has(matchMode);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1.4fr) minmax(140px, 0.7fr) minmax(0, 1fr) auto',
        gridTemplateAreas: '"drag question operator value remove"',
        gap: 1,
        alignItems: 'center',
        minWidth: 0,
        '& .MuiFormControl-root': compactControlSx,
        '@media (max-width: 700px)': {
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          gridTemplateAreas: `
            "drag question remove"
            "operator operator operator"
            "value value value"
          `,
        },
      }}>
      <Box
        {...(dragHandleProps || {})}
        sx={{
          gridArea: 'drag',
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          cursor: disabled ? 'default' : 'grab',
          opacity: disabled ? 0.4 : 0.7,
        }}>
        <DragIndicator fontSize="small" />
      </Box>

      <Box sx={{gridArea: 'question', minWidth: 0}}>
        <Controller
          control={control}
          name={`${baseName}.questionKey`}
          render={({field}) => (
            <Select {...field} value={field.value ?? ''} size="small" disabled={disabled} fullWidth displayEmpty>
              <MenuItem value="">
                <em>Cualquier respuesta</em>
              </MenuItem>
              {questionOptions.map((q) => (
                <MenuItem key={q.key} value={q.key}>
                  {q.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      <Box sx={{gridArea: 'operator', minWidth: 0}}>
        <Controller
          control={control}
          name={`${baseName}.matchMode`}
          render={({field}) => (
            <Select {...field} value={field.value || 'CONTAINS'} size="small" disabled={disabled} fullWidth>
              {MATCH_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Box>

      <Box sx={{gridArea: 'value', minWidth: 0}}>
        {needsMatchValue ? (
          <Controller
            control={control}
            name={`${baseName}.matchText`}
            render={({field}) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                size="small"
                fullWidth
                disabled={disabled}
                type={isNumericOperator ? 'number' : 'text'}
                placeholder={
                  isNumericOperator ? 'Ej: 10' : matchMode === 'REGEX' ? 'Ej: ^(si|sí)$' : 'observación negativa'
                }
                inputProps={isNumericOperator ? {min: undefined, step: 'any'} : {}}
                sx={compactControlSx}
              />
            )}
          />
        ) : null}
      </Box>

      <Box sx={{gridArea: 'remove'}}>
        <Tooltip title="Quitar condición">
          <span>
            <IconButton size="small" disabled={disabled || !canRemove} onClick={onRemove}>
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

function ConditionGroupEditor({
  groupsName,
  groupIndex,
  questionOptions,
  disabled,
  showGroupChrome,
  canRemoveGroup,
  onRemoveGroup,
  extraActions,
}: {
  groupsName: string;
  groupIndex: number;
  questionOptions: QuestionOption[];
  disabled: boolean;
  showGroupChrome: boolean;
  canRemoveGroup: boolean;
  onRemoveGroup: () => void;
  extraActions?: React.ReactNode;
}) {
  const {control} = useFormContext();
  const {fields, append, remove} = useFieldArray({
    control,
    name: `${groupsName}.${groupIndex}.conditions`,
  });

  return (
    <Droppable droppableId={`${groupsName}.${groupIndex}`} type="CONDITION" isDropDisabled={disabled}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            minWidth: 0,
            ...(showGroupChrome
              ? {
                  ...nestedPanelSx,
                  bgcolor: snapshot.isDraggingOver ? 'action.hover' : nestedPanelSx.bgcolor,
                }
              : snapshot.isDraggingOver
                ? {bgcolor: 'action.hover', borderRadius: 1}
                : null),
          }}>
          {fields.map((row, i) => (
            <React.Fragment key={row.id}>
              {i > 0 && (
                <CombinatorToggle
                  name={`${groupsName}.${groupIndex}.combinator`}
                  disabled={disabled}
                  fallback="AND"
                  inset
                />
              )}
              <Draggable draggableId={row.id} index={i} isDragDisabled={disabled}>
                {(dragProvided, dragSnapshot) => (
                  <Box
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    sx={{
                      bgcolor: dragSnapshot.isDragging ? 'background.paper' : 'transparent',
                      borderRadius: 1,
                    }}>
                    <ConditionRow
                      baseName={`${groupsName}.${groupIndex}.conditions.${i}`}
                      questionOptions={questionOptions}
                      disabled={disabled}
                      dragHandleProps={dragProvided.dragHandleProps}
                      canRemove={fields.length > 1 || canRemoveGroup}
                      onRemove={() => {
                        if (fields.length <= 1) {
                          if (canRemoveGroup) onRemoveGroup();
                          return;
                        }
                        remove(i);
                      }}
                    />
                  </Box>
                )}
              </Draggable>
            </React.Fragment>
          ))}
          {provided.placeholder}

          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1}}>
            <Button size="small" disabled={disabled} onClick={() => append(emptyLeaf())} startIcon={<AddIcon />}>
              Agregar condición
            </Button>
            {extraActions}
          </Box>
        </Box>
      )}
    </Droppable>
  );
}

function GroupsBuilder({
  ruleName,
  questionOptions,
  disabled,
}: {
  ruleName: string;
  questionOptions: QuestionOption[];
  disabled: boolean;
}) {
  const {control, setValue, getValues} = useFormContext();
  const groupsName = `${ruleName}.groups`;
  const {fields, append, remove} = useFieldArray({control, name: groupsName});

  useEffect(() => {
    const groups = getValues(groupsName);
    if (Array.isArray(groups) && groups.length) return;
    const rule = getValues(ruleName);
    setValue(groupsName, wrapLegacyGroups(rule), {shouldDirty: false, shouldValidate: false});
    if (!getValues(`${ruleName}.groupCombinator`)) {
      setValue(`${ruleName}.groupCombinator`, 'OR', {shouldDirty: false, shouldValidate: false});
    }
  }, [getValues, groupsName, ruleName, setValue]);

  const showChrome = fields.length > 1;

  const onDragEnd = (result: {
    source: {droppableId: string; index: number};
    destination?: {droppableId: string; index: number} | null;
  }) => {
    const {source, destination} = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const groups = getValues(groupsName);
    if (!Array.isArray(groups)) return;

    const sourceIndex = groups.findIndex((_, i) => `${groupsName}.${i}` === source.droppableId);
    const destIndex = groups.findIndex((_, i) => `${groupsName}.${i}` === destination.droppableId);
    if (sourceIndex < 0 || destIndex < 0) return;

    const next = groups.map((g: any) => ({
      combinator: g?.combinator === 'OR' ? 'OR' : 'AND',
      conditions: Array.isArray(g?.conditions) ? [...g.conditions] : [emptyLeaf()],
    }));

    const [moved] = next[sourceIndex].conditions.splice(source.index, 1);
    if (!moved) return;
    next[destIndex].conditions.splice(destination.index, 0, moved);

    const cleaned = next.filter((g) => g.conditions.length > 0);
    setValue(groupsName, cleaned.length ? cleaned : [emptyGroup()], {shouldDirty: true, shouldValidate: false});
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, width: '100%'}}>
        {fields.map((row, i) => (
          <React.Fragment key={row.id}>
            {i > 0 && <CombinatorToggle name={`${ruleName}.groupCombinator`} disabled={disabled} fallback="OR" />}
            <ConditionGroupEditor
              groupsName={groupsName}
              groupIndex={i}
              questionOptions={questionOptions}
              disabled={disabled}
              showGroupChrome={showChrome}
              canRemoveGroup={fields.length > 1}
              onRemoveGroup={() => remove(i)}
              extraActions={
                !showChrome ? (
                  <Button size="small" disabled={disabled} onClick={() => append(emptyGroup())} startIcon={<AddIcon />}>
                    Agregar grupo
                  </Button>
                ) : undefined
              }
            />
          </React.Fragment>
        ))}

        {showChrome && (
          <Box>
            <Button size="small" disabled={disabled} onClick={() => append(emptyGroup())} startIcon={<AddIcon />}>
              Agregar grupo
            </Button>
          </Box>
        )}
      </Box>
    </DragDropContext>
  );
}

export function ConditionalRulesTable({name, formQuestionsPath, disabled}: Props) {
  const {control, setValue, getValues} = useFormContext();

  const {fields, append, remove} = useFieldArray({
    control,
    name,
  });

  const formato = useWatch({control, name: formQuestionsPath});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const initializedRef = useRef(false);
  const pendingExpandRef = useRef(false);

  const questionOptions = useMemo(() => {
    return collectFormatoQuestionOptions(Array.isArray(formato) ? formato : []).map((q) => ({
      key: q.key,
      title: q.title,
      label: q.label,
    }));
  }, [formato]);

  useEffect(() => {
    const validKeys = new Set(questionOptions.map((q) => q.key));
    const rules = getValues(name);
    if (!Array.isArray(rules)) return;

    rules.forEach((rule: any, index: number) => {
      const current = normalizeMulti(rule?.emailHighlightQuestionKeys);
      if (!current.length) return;
      const cleaned = current.filter((k) => validKeys.has(k));
      if (cleaned.length !== current.length) {
        setValue(`${name}.${index}.emailHighlightQuestionKeys`, cleaned, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    });
  }, [questionOptions, name, getValues, setValue]);

  useEffect(() => {
    const rules = getValues(name);
    if (!Array.isArray(rules)) return;
    rules.forEach((rule: any, index: number) => {
      if (Array.isArray(rule?.groups) && rule.groups.length) {
        if (!rule.groupCombinator) {
          setValue(`${name}.${index}.groupCombinator`, 'OR', {shouldDirty: false, shouldValidate: false});
        }
        return;
      }
      setValue(`${name}.${index}.groups`, wrapLegacyGroups(rule), {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue(`${name}.${index}.groupCombinator`, rule?.groupCombinator === 'AND' ? 'AND' : 'OR', {
        shouldDirty: false,
        shouldValidate: false,
      });
    });
  }, [fields.length, name, getValues, setValue]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!fields.length) return;
    initializedRef.current = true;
    if (fields.length === 1) {
      setExpandedIds(new Set([fields[0].id]));
    }
  }, [fields]);

  useEffect(() => {
    if (!pendingExpandRef.current || !fields.length) return;
    pendingExpandRef.current = false;
    const last = fields[fields.length - 1];
    setExpandedIds((prev) => new Set(prev).add(last.id));
  }, [fields]);

  const addRule = () => {
    pendingExpandRef.current = true;
    append(emptyRule());
  };

  const duplicateRule = (index: number) => {
    const allRules = (control as any)._formValues?.[name] || getValues(name) || [];
    const src = allRules?.[index];
    if (!src) return;
    pendingExpandRef.current = true;
    append(cloneRule(src));
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Paper variant="outlined" sx={{p: 2, overflow: 'hidden'}}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1.5} flexWrap="wrap">
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography variant="h6" sx={{fontSize: 16}}>
            Reglas de acciones condicionales
          </Typography>
          <Typography variant="body2" sx={{opacity: 0.7, mt: 0.5}}>
            Solicitar firma al enviar a revisión; el resto al cerrar.
          </Typography>
        </Box>

        <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={disabled} onClick={addRule}>
          Agregar regla
        </Button>
      </Box>

      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0}}>
        {fields.map((row, index) => (
          <RuleCard
            key={row.id}
            index={index}
            rulesName={name}
            questionOptions={questionOptions}
            disabled={!!disabled}
            expanded={expandedIds.has(row.id)}
            onToggle={() => toggleExpanded(row.id)}
            onDuplicate={() => duplicateRule(index)}
            onRemove={() => remove(index)}
          />
        ))}

        {fields.length === 0 && (
          <Typography variant="body2" sx={{opacity: 0.7, py: 2}}>
            No hay reglas configuradas.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function RuleCard({
  index,
  rulesName,
  questionOptions,
  disabled,
  expanded,
  onToggle,
  onDuplicate,
  onRemove,
}: {
  index: number;
  rulesName: string;
  questionOptions: QuestionOption[];
  disabled: boolean;
  expanded: boolean;
  onToggle: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const {control} = useFormContext();
  const ruleName = `${rulesName}.${index}`;

  const enabled = useWatch({control, name: `${ruleName}.enabled`});
  const groups = useWatch({control, name: `${ruleName}.groups`});
  const groupCombinator = useWatch({control, name: `${ruleName}.groupCombinator`});
  const questionKey = useWatch({control, name: `${ruleName}.questionKey`});
  const matchText = useWatch({control, name: `${ruleName}.matchText`});
  const matchMode = useWatch({control, name: `${ruleName}.matchMode`});
  const actionsRaw = useWatch({control, name: `${ruleName}.actions`});
  const actions = normalizeMulti(actionsRaw) as RuleActionType[];

  const hasTaskAction = actions.includes('TASK');
  const hasEmailAction = actions.includes('EMAIL');
  const disableRecipients = disabled || !actions.length;

  const conditionTitle = summarizeConditions(
    {
      groups,
      groupCombinator,
      questionKey,
      matchText,
      matchMode,
    },
    questionOptions,
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      sx={{
        boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        '&:before': {display: 'none'},
        overflow: 'hidden',
        minWidth: 0,
        opacity: enabled === false ? 0.72 : 1,
      }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 48,
          px: 2,
          '&.Mui-expanded': {minHeight: 48},
          '& .MuiAccordionSummary-content': {
            my: 1,
            minWidth: 0,
            overflow: 'hidden',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          },
        }}>
        <Box
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
          sx={{flexShrink: 0, display: 'flex', alignItems: 'center'}}>
          <Controller
            control={control}
            name={`${ruleName}.enabled`}
            render={({field}) => (
              <Switch {...field} checked={!!field.value} disabled={disabled} size="small" />
            )}
          />
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {conditionTitle}
        </Typography>
        {actions.length > 0 && (
          <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0, maxWidth: '40%'}}>
            {actions.map((v) => {
              const lbl = ACTION_OPTIONS.find((o) => o.value === v)?.label ?? v;
              return <Chip key={v} size="small" label={lbl} />;
            })}
          </Box>
        )}
        <Box display="flex" onClick={(e) => e.stopPropagation()} sx={{flexShrink: 0}}>
          <Tooltip title="Duplicar regla">
            <span>
              <IconButton disabled={disabled} onClick={onDuplicate} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Eliminar regla">
            <span>
              <IconButton disabled={disabled} onClick={onRemove} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{pt: 0, pb: 2, px: 2}}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
          <Box sx={{minWidth: 0}}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Condiciones
            </Typography>
            <GroupsBuilder ruleName={ruleName} questionOptions={questionOptions} disabled={disabled} />
          </Box>

          <Divider />

          <Box sx={{minWidth: 0}}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Acciones
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0}}>
              <Controller
                control={control}
                name={`${ruleName}.actions`}
                render={({field}) => (
                  <FormControl sx={{mt: '0px !important'}} fullWidth size="small">
                    <Select
                      multiple
                      disabled={disabled}
                      displayEmpty
                      value={normalizeMulti(field.value)}
                      onChange={(e) => field.onChange(normalizeMulti((e.target as any).value))}
                      renderValue={(selected: any) => {
                        const arr = normalizeMulti(selected);
                        if (!arr.length) return <span style={{opacity: 0.6}}>Selecciona acciones</span>;
                        return (
                          <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                            {arr.map((v: string) => {
                              const lbl = ACTION_OPTIONS.find((o) => o.value === v)?.label ?? v;
                              return <Chip key={v} size="small" label={lbl} />;
                            })}
                          </Box>
                        );
                      }}>
                      {ACTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              {hasTaskAction && (
                <Box sx={{...nestedPanelSx, display: 'flex', flexDirection: 'column', gap: 1.5}}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) minmax(88px, 110px)',
                      gap: 1,
                      alignItems: 'center',
                      '@media (max-width: 700px)': {
                        gridTemplateColumns: '1fr',
                      },
                    }}>
                    <Box sx={{minWidth: 0}}>
                      <ModeloTareaAutocompleteFormItem
                        name={`${ruleName}.modeloTarea`}
                        label="Tarea"
                        menuPortalTarget={document.body}
                        disabled={disabled}
                        mt0
                        idLabelOnly
                      />
                    </Box>

                    <Controller
                      control={control}
                      name={`${ruleName}.taskDueDays`}
                      render={({field}) => (
                        <TextField
                          {...field}
                          size="small"
                          disabled={disabled}
                          type="number"
                          label="Plazo (días)"
                          inputProps={{min: 0}}
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{display: 'block', mb: 0.75, opacity: 0.75}}>
                      Validadores (opcional)
                    </Typography>
                    <RecipientsEditor
                      baseName={`${ruleName}.validatorRecipients`}
                      questionOptions={questionOptions}
                      ruleActions={actions}
                      disabled={disabled}
                      hideActionSelect
                      allowedModes={VALIDATOR_RECIPIENT_MODES}
                      addButtonLabel="Agregar validador"
                    />
                  </Box>
                </Box>
              )}

              {hasEmailAction && (
                <Box sx={{...nestedPanelSx, display: 'flex', flexDirection: 'column', gap: 1.5}}>
                  <Controller
                    control={control}
                    name={`${ruleName}.attachTaskPdf`}
                    render={({field}) => (
                      <FormControlLabel
                        sx={{ml: 0, mr: 0, alignItems: 'center'}}
                        control={
                          <Switch
                            size="small"
                            disabled={disabled}
                            checked={field.value !== false}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">Adjuntar PDF de la tarea</Typography>
                        }
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${ruleName}.emailSeparateRecipients`}
                    render={({field}) => (
                      <FormControlLabel
                        sx={{ml: 0, mr: 0, alignItems: 'flex-start'}}
                        control={
                          <Switch
                            size="small"
                            disabled={disabled}
                            checked={field.value === true}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              Enviar un correo por destinatario
                            </Typography>
                            {field.value !== true && (
                              <Typography variant="caption" sx={{opacity: 0.75, display: 'block'}}>
                                Desactivado: Todos los destinatarios estarán en copia (CC)
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    )}
                  />
                  <Typography variant="caption" sx={{display: 'block', mb: 0.5, opacity: 0.75}}>
                    Preguntas a destacar en el correo (opcional)
                  </Typography>
                  <Controller
                    control={control}
                    name={`${ruleName}.emailHighlightQuestionKeys`}
                    render={({field}) => (
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          disabled={disabled}
                          displayEmpty
                          value={normalizeMulti(field.value)}
                          onChange={(e) => field.onChange(normalizeMulti((e.target as any).value))}
                          renderValue={(selected: any) => {
                            const arr = normalizeMulti(selected);
                            if (!arr.length) {
                              return <span style={{opacity: 0.6}}>Usar pregunta que gatilla la condición</span>;
                            }
                            return (
                              <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                                {arr.map((key: string) => {
                                  const lbl = questionOptions.find((o) => o.key === key)?.label ?? key;
                                  return <Chip key={key} size="small" label={lbl} />;
                                })}
                              </Box>
                            );
                          }}>
                          {questionOptions.map((q) => (
                            <MenuItem key={q.key} value={q.key}>
                              {q.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Divider />

          <Box sx={{minWidth: 0}}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Destinatarios
            </Typography>
            <RecipientsEditor
              baseName={`${ruleName}.recipients`}
              questionOptions={questionOptions}
              ruleActions={actions}
              disabled={disableRecipients}
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function RecipientsEditor({
  baseName,
  questionOptions,
  ruleActions,
  disabled,
  hideActionSelect,
  allowedModes,
  addButtonLabel,
}: {
  baseName: string;
  questionOptions: QuestionOption[];
  ruleActions: RuleActionType[];
  disabled?: boolean;
  hideActionSelect?: boolean;
  allowedModes?: RecipientMode[];
  addButtonLabel?: string;
}) {
  const {control} = useFormContext();
  const {fields, append, remove} = useFieldArray({control, name: baseName});

  const recipientsValues = useWatch({control, name: baseName}) || [];

  const actionOptionsForRow = useMemo(
    () => ACTION_OPTIONS.filter((o) => ruleActions.includes(o.value as RuleActionType)),
    [ruleActions],
  );

  const addRecipient = () => {
    if (hideActionSelect) {
      append({mode: 'FROM_QUESTION', questionKey: ''});
      return;
    }
    const initialActions = ruleActions.length ? [...ruleActions] : [];
    append({mode: 'FROM_QUESTION', questionKey: '', actions: initialActions});
  };

  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{width: '100%', minWidth: 0}}>
      {fields.map((r, i) => {
        const rVal = recipientsValues?.[i] || {};
        const mode: RecipientMode = rVal?.mode || 'FROM_QUESTION';

        return (
          <Box key={r.id} display="flex" flexDirection="column" gap={1} sx={nestedPanelSx}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(140px, 180px) minmax(0, 1fr) auto',
                gridTemplateAreas: '"mode value remove"',
                gap: 1,
                alignItems: 'center',
                minWidth: 0,
                '@media (max-width: 700px)': {
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  gridTemplateAreas: `
                    "mode remove"
                    "value value"
                  `,
                },
              }}>
              <Box sx={{gridArea: 'mode', minWidth: 0}}>
                <Controller
                  control={control}
                  name={`${baseName}.${i}.mode`}
                  render={({field}) => (
                    <Select {...field} size="small" disabled={disabled} fullWidth>
                      {RECIPIENT_MODE_OPTIONS.filter(
                        (opt) =>
                          !allowedModes ||
                          allowedModes.includes(opt.value) ||
                          opt.value === mode,
                      ).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>

              <Box sx={{gridArea: 'value', minWidth: 0, width: '100%'}}>
                {mode === 'FROM_QUESTION' ? (
                  <Controller
                    control={control}
                    name={`${baseName}.${i}.questionKey`}
                    render={({field}) => (
                      <Select {...field} value={field.value ?? ''} size="small" disabled={disabled} fullWidth displayEmpty>
                        <MenuItem value="">
                          <em>Selecciona una pregunta</em>
                        </MenuItem>
                        {questionOptions.map((q) => (
                          <MenuItem key={q.key} value={q.key}>
                            {q.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                ) : mode === 'USER' ? (
                  <UserAutocompleteFormItem
                    name={`${baseName}.${i}.value`}
                    menuPortalTarget={document.body}
                    disabled={disabled}
                    minWidth={'100%'}
                    mt0
                    idLabelOnly
                  />
                ) : mode === 'LOCATION_RESPONSIBLE' ? (
                  <UbicacionAutocompleteFormItem
                    name={`${baseName}.${i}.value`}
                    menuPortalTarget={document.body}
                    disabled={disabled}
                    minWidth={'100%'}
                    mt0
                    idLabelOnly
                  />
                ) : mode === 'TEAM' ? (
                  <AsignadorFuncionAutocompleteFormItem
                    name={`${baseName}.${i}.value`}
                    menuPortalTarget={document.body}
                    disabled={disabled}
                    minWidth={'100%'}
                    mt0
                    idLabelOnly
                  />
                ) : mode === 'EMAIL' ? (
                  <Controller
                    control={control}
                    name={`${baseName}.${i}.value`}
                    render={({field}) => (
                      <TextField
                        {...field}
                        sx={{mt: '0px !important'}}
                        size="small"
                        disabled={disabled}
                        fullWidth
                        placeholder="email@dominio.cl"
                      />
                    )}
                  />
                ) : null}
              </Box>

              <Box sx={{gridArea: 'remove'}}>
                <Tooltip title="Quitar destinatario">
                  <span>
                    <IconButton size="small" disabled={disabled} onClick={() => remove(i)}>
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {!hideActionSelect && actionOptionsForRow.length > 0 && (
              <Controller
                control={control}
                name={`${baseName}.${i}.actions`}
                render={({field}) => (
                  <FormControl size="small" fullWidth disabled={disabled}>
                    <Select
                      multiple
                      displayEmpty
                      value={normalizeMulti(field.value).filter((v) =>
                        ruleActions.includes(v as RuleActionType),
                      )}
                      onChange={(e) => field.onChange(normalizeMulti((e.target as any).value))}
                      renderValue={(selected: unknown) => {
                        const arr = normalizeMulti(selected);
                        if (!arr.length) {
                          return (
                            <Typography variant="caption" sx={{opacity: 0.72}}>
                              Todas las acciones de la regla
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                            {arr.map((v: string) => {
                              const lbl = ACTION_OPTIONS.find((o) => o.value === v)?.label ?? v;
                              return <Chip key={v} size="small" label={lbl} />;
                            })}
                          </Box>
                        );
                      }}>
                      {actionOptionsForRow.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}
          </Box>
        );
      })}

      <Box>
        <Button size="small" disabled={disabled} onClick={addRecipient} startIcon={<AddIcon />}>
          {addButtonLabel || 'Agregar destinatario'}
        </Button>
      </Box>
    </Box>
  );
}
